/**
 * Service for checking feature availability based on user's subscription tier
 */

// List of all possible features
export const Features = {
  // Core features (free tier only has these)
  SLACK_INTEGRATION: 'slack_integration',
  MS_TEAMS_INTEGRATION: 'ms_teams_integration',
  SEND_APPRECIATION: 'send_appreciation',
  VIEW_APPRECIATIONS: 'view_appreciations',
  VIEW_LEADERBOARD: 'view_leaderboard',
  APPRECIATION_LINK: 'appreciation_link',
  WEEKLY_APPRECIATION_WINNERS: 'weekly_appreciation_winners',
  EMAIL_NOTIFICATIONS: 'email_notifications',

  // Premium features
  ANALYTICS: 'analytics',
  BULK_USER_IMPORT: 'bulk_user_import',
  CUSTOM_COMPANY_VALUES: 'custom_company_values',
  DEPARTMENTAL_LEADERBOARDS: 'departmental_leaderboards',
  USER_BADGES: 'user_badges',
  SSO_AUTH: 'sso_auth',
  CUSTOM_BRANDING: 'custom_branding',
  CUSTOM_APPRECIATION: 'custom_appreciation',
  ADVANCED_SECURITY: 'advanced_security',
};

// Map of features to the plan they're available in
export const FeaturePlans = {
  // Core features
  [Features.SLACK_INTEGRATION]: 'Free',
  [Features.MS_TEAMS_INTEGRATION]: 'Free',
  [Features.SEND_APPRECIATION]: 'Free',
  [Features.VIEW_APPRECIATIONS]: 'Free',
  [Features.VIEW_LEADERBOARD]: 'Free',
  [Features.APPRECIATION_LINK]: 'Free',
  [Features.WEEKLY_APPRECIATION_WINNERS]: 'Free',
  [Features.EMAIL_NOTIFICATIONS]: 'Free',

  // All premium features
  [Features.ANALYTICS]: 'Pro',
  [Features.BULK_USER_IMPORT]: 'Pro',
  [Features.CUSTOM_COMPANY_VALUES]: 'Pro',
  [Features.DEPARTMENTAL_LEADERBOARDS]: 'Pro',
  [Features.USER_BADGES]: 'Pro',
  [Features.SSO_AUTH]: 'Pro',
  [Features.CUSTOM_BRANDING]: 'Pro',
  [Features.CUSTOM_APPRECIATION]: 'Pro',
  [Features.ADVANCED_SECURITY]: 'Pro',
};

// Map of plans to their display names
export const PlanNames = {
  'Free': 'Free',
  'Pro': 'Pro',
};

// Store available features from the backend
let availableFeatures = [];

// Store limit information
let limits = {
  users: {
    current: 0,
    max: 10,
    within_limit: true
  },
  monthly_appreciations: {
    current: 0,
    max: 100,
    within_limit: true
  }
};

/**
 * Set the available features for the current user
 * @param {string[]} features List of feature keys
 */
export const setAvailableFeatures = (features) => {
  availableFeatures = features || [];
};

/**
 * Set the limit information
 * @param {Object} limitInfo Limit information object
 */
export const setLimits = (limitInfo) => {
  limits = limitInfo || limits;
};

/**
 * Check if a specific feature is available
 * @param {string} featureKey The feature key to check
 * @returns {boolean} True if the feature is available
 */
export const hasFeature = (featureKey) => {
  return availableFeatures.includes(featureKey);
};

/**
 * Gets upgrade information for a feature
 * @param {string} featureKey The feature to check
 * @returns {Object|null} Upgrade info or null if feature is available
 */
export const getUpgradeInfo = (featureKey) => {
  if (hasFeature(featureKey)) {
    return null; // Feature is available, no upgrade needed
  }

  const requiredPlan = FeaturePlans[featureKey] || 'Pro';

  return {
    plan: requiredPlan,
    message: `This feature requires a ${requiredPlan} subscription`,
  };
};

/**
 * Get limit information
 * @param {string} limitType Type of limit ('users' or 'monthly_appreciations')
 * @returns {Object} Limit information
 */
export const getLimitInfo = (limitType) => {
  return limits[limitType] || { within_limit: true };
};

/**
 * Check if a specific limit is being approached (80% or more)
 * @param {string} limitType Type of limit ('users' or 'monthly_appreciations')
 * @returns {boolean} True if limit is being approached
 */
export const isLimitApproaching = (limitType) => {
  const limit = limits[limitType];
  if (!limit || limit.max === null) return false;

  return limit.current >= (limit.max * 0.8);
};

/**
 * Get formatted limit usage text
 * @param {string} limitType Type of limit ('users' or 'monthly_appreciations')
 * @returns {string} Formatted limit text
 */
export const getLimitText = (limitType) => {
  const limit = limits[limitType];
  if (!limit) return '';
  if (limit.max === null) return 'Unlimited';

  return `${limit.current}/${limit.max}`;
};